import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type { ReservationType } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { RESERVATION_TYPE_LABELS } from "./resourceVocabulary";

const TYPES: ReservationType[] = [
  "TRAINING",
  "INTERNAL_MEETINGS",
  "ADMIN",
  "SUPPORT_ROTA",
  "OTHER",
];

/**
 * Setting aside part of somebody's time before projects claim it (§40).
 *
 * <p>Reserved time is the difference between a plan that works and one that assumes everybody
 * spends five days a week on project work. A developer on the support rota two days a fortnight
 * is genuinely not available for those days, and a planner that cannot say so will keep promising
 * their time and keep being wrong.
 *
 * <p>Open-ended is allowed and is the common case — a standing support rota has no end date.
 * Ending one is a date rather than a delete, so the weeks it did apply to still add up.
 */
export function ReservationDrawer({
  employee,
  open,
  onClose,
  onSaved,
}: {
  employee: { employeeId: string; fullName: string } | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [reservationType, setReservationType] = useState<ReservationType>("TRAINING");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [percentage, setPercentage] = useState("20");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setReservationType("TRAINING");
    setStart(new Date().toISOString().slice(0, 10));
    setEnd("");
    setPercentage("20");
    setNote("");
    setError(null);
  }, [open]);

  const share = Number(percentage);
  const shareValid = !Number.isNaN(share) && share > 0 && share <= 100;
  const endsBeforeItStarts = end !== "" && end < start;
  const canSubmit =
    employee != null && start !== "" && shareValid && !endsBeforeItStarts;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/v1/resources/reservations", {
        employeeId: employee?.employeeId,
        reservationType,
        reservationStart: start,
        reservationEnd: end === "" ? null : end,
        percentage: share,
        note: note.trim() === "" ? null : note.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ["person-reservations"] });
      await queryClient.invalidateQueries({ queryKey: ["person-capacity"] });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Reserve time"
      subtitle={employee?.fullName}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : "Reserve"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="What for" required>
          <Select
            value={reservationType}
            onChange={(event) => setReservationType(event.target.value as ReservationType)}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {RESERVATION_TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="From" required>
            <Input type="date" value={start} onChange={(event) => setStart(event.target.value)} />
          </Field>
          <Field label="Until" hint="Leave blank if it is ongoing.">
            <Input type="date" value={end} onChange={(event) => setEnd(event.target.value)} />
          </Field>
        </div>

        {endsBeforeItStarts && (
          <InlineAlert tone="error">It cannot end before it starts.</InlineAlert>
        )}

        <Field
          label="How much of their week"
          required
          hint="A percentage. Two days a week is about 40."
        >
          <Input
            type="number"
            min={1}
            max={100}
            value={percentage}
            onChange={(event) => setPercentage(event.target.value)}
          />
        </Field>

        {!shareValid && percentage !== "" && (
          <InlineAlert tone="error">That has to be between 1 and 100.</InlineAlert>
        )}

        <Field label="Note" hint="What this actually is, for whoever reads the plan later.">
          <Textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}
